import { graphql, Link } from 'gatsby';
import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import SEO from '../components/SEO';
import Footer from '../sections/Footer';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Hero = styled.section`
  position: relative;
  height: 80vh;
  min-height: 540px;
  overflow: hidden;
  background: #0b1f3a;

  img.hero-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    opacity: 0.55;
  }

  .hero-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(11, 31, 58, 0.45) 0%,
      rgba(11, 31, 58, 0.85) 100%
    );
    z-index: 1;
  }

  .hero-content {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0 24px;
  }

  .hero-cat {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 22px;
    display: inline-flex;
    align-items: center;
    gap: 14px;
    animation: ${fadeUp} 0.8s ease 0.1s both;

    &::before,
    &::after {
      content: '';
      width: 28px;
      height: 1px;
      background: rgba(240, 128, 32, 0.6);
    }
  }

  .hero-title {
    font-size: clamp(2rem, 5vw, 3.8rem);
    font-weight: 700;
    color: #fff;
    line-height: 1.15;
    letter-spacing: -0.01em;
    max-width: 980px;
    margin: 0 0 28px;
    animation: ${fadeUp} 0.8s ease 0.25s both;
  }

  .hero-meta {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.65);
    animation: ${fadeUp} 0.8s ease 0.4s both;

    .dot {
      width: 4px;
      height: 4px;
      background: rgba(255, 255, 255, 0.4);
      border-radius: 50%;
    }
  }
`;

const Crumbs = styled.div`
  background: #fff;
  border-bottom: 1px solid #e7eef6;

  .crumbs-inner {
    max-width: 1320px;
    margin: 0 auto;
    padding: 20px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;

    @media (max-width: 768px) {
      padding: 16px 20px;
    }
  }

  .crumb-back {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #5b6b7c;
    transition: color 0.25s ease;

    svg {
      width: 14px;
      height: 14px;
      stroke: currentColor;
      fill: none;
      stroke-width: 1.5;
      transition: transform 0.25s ease;
    }

    &:hover {
      color: #f08020;
      svg {
        transform: translateX(-3px);
      }
    }
  }

  .crumb-cat {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #1378e5;
  }
`;

const Description = styled.section`
  background: #fff;
  padding: 96px 32px 56px;

  @media (max-width: 768px) {
    padding: 64px 20px 32px;
  }

  .desc-inner {
    max-width: 1100px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 56px;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }

  .desc-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #f08020;
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 8px;

    &::before {
      content: '';
      width: 32px;
      height: 1px;
      background: #f08020;
    }
  }

  .desc-body {
    font-size: 16px;
    color: #1b2733;
    line-height: 1.95;
    font-weight: 300;
    white-space: pre-line;

    @media (max-width: 768px) {
      font-size: 14.5px;
      line-height: 1.85;
    }
  }
`;

const Gallery = styled.section`
  background: #fff;
  padding: 96px 32px 120px;

  @media (max-width: 768px) {
    padding: 64px 20px 72px;
  }

  .gallery-inner {
    max-width: 1320px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 24px;

    @media (max-width: 768px) {
      gap: 16px;
    }
  }

  .gallery-section-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #f08020;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;

    &::before {
      content: '';
      width: 32px;
      height: 1px;
      background: #f08020;
    }
  }
`;

const Row = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: ${({ cols }) => `repeat(${cols}, 1fr)`};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Tile = styled.div`
  position: relative;
  overflow: hidden;
  cursor: zoom-in;
  background: #f2f6fc;
  aspect-ratio: ${({ ratio }) => ratio || '4 / 3'};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .tile-num {
    position: absolute;
    top: 16px;
    left: 16px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.18em;
    color: #fff;
    background: rgba(11, 31, 58, 0.6);
    backdrop-filter: blur(8px);
    padding: 6px 10px;
    z-index: 2;
    opacity: 0;
    transform: translateY(-4px);
    transition: opacity 0.3s ease, transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.04);
  }

  &:hover .tile-num {
    opacity: 1;
    transform: translateY(0);
  }
`;

const CtaSection = styled.section`
  background: #0b1f3a;
  padding: 96px 32px;
  text-align: center;
  color: #fff;

  @media (max-width: 768px) {
    padding: 64px 20px;
  }

  .cta-eyebrow {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 18px;
  }

  .cta-title {
    font-size: clamp(1.5rem, 3vw, 2.2rem);
    font-weight: 400;
    line-height: 1.3;
    max-width: 640px;
    margin: 0 auto 36px;
  }

  .cta-actions {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: center;
  }

  .cta-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 14px 32px;
    border: 1px solid;
    transition: all 0.3s ease;

    svg {
      width: 14px;
      height: 14px;
      stroke: currentColor;
      fill: none;
      stroke-width: 1.5;
      transition: transform 0.3s ease;
    }

    &.primary {
      background: #f08020;
      border-color: #f08020;
      color: #fff;

      &:hover {
        background: #d76c10;
        border-color: #d76c10;
        svg {
          transform: translateX(4px);
        }
      }
    }

    &.ghost {
      background: transparent;
      border-color: rgba(255, 255, 255, 0.3);
      color: #fff;

      &:hover {
        border-color: #fff;
        svg {
          transform: translateX(-4px);
        }
      }
    }
  }
`;

const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;

  img {
    max-width: 92vw;
    max-height: 88vh;
    object-fit: contain;
    display: block;
  }

  .lb-counter {
    position: absolute;
    top: 24px;
    left: 24px;
    font-size: 12px;
    letter-spacing: 0.18em;
    color: rgba(255, 255, 255, 0.7);
  }

  .lb-close,
  .lb-prev,
  .lb-next {
    position: absolute;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.25s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.18);
    }

    svg {
      width: 20px;
      height: 20px;
      stroke: currentColor;
      fill: none;
      stroke-width: 1.5;
    }
  }

  .lb-close {
    top: 24px;
    right: 24px;
  }

  .lb-prev {
    left: 24px;
  }

  .lb-next {
    right: 24px;
  }
`;

export const query = graphql`
  query ProjectBySlug($slug: String!) {
    sanityProject(slug: { current: { eq: $slug } }) {
      _id
      title
      description
      slug {
        current
      }
      category {
        name
      }
      coverImage {
        asset {
          _id
          url
        }
      }
      imageGallery {
        _key
        asset {
          _id
          url
        }
      }
    }
  }
`;

function buildRows(images) {
  // Smart layout: 1 = full, otherwise pairs, with every 3rd row full-width
  // for rhythm. First and last rows stay in the 2-up grid.
  if (images.length === 0) return [];
  if (images.length === 1) return [{ items: images, cols: 1, ratio: '16 / 9' }];

  const rows = [];
  let i = 0;
  let rowIdx = 0;
  while (i < images.length) {
    const left = images.length - i;
    // Only break rhythm with a full-width row when it leaves an even number
    // behind, so the gallery still ends on a pair rather than a stray single.
    const isFullRow = rowIdx % 3 === 2 && left >= 3 && left % 2 === 1;
    if (isFullRow) {
      rows.push({ items: [images[i]], cols: 1, ratio: '21 / 9' });
      i += 1;
    } else if (i + 1 < images.length) {
      rows.push({ items: [images[i], images[i + 1]], cols: 2, ratio: '4 / 3' });
      i += 2;
    } else {
      // Trailing odd image keeps the 2-up grid rather than going full-width
      rows.push({ items: [images[i]], cols: 2, ratio: '4 / 3' });
      i += 1;
    }
    rowIdx += 1;
  }
  return rows;
}

export default function PortfolioPost({ data }) {
  const project = data?.sanityProject;
  const gallery = project?.imageGallery || [];

  const [activeIdx, setActiveIdx] = useState(null);
  const close = useCallback(() => setActiveIdx(null), []);
  const prev = useCallback(
    e => {
      e?.stopPropagation();
      setActiveIdx(i =>
        i === null ? null : (i - 1 + gallery.length) % gallery.length
      );
    },
    [gallery.length]
  );
  const next = useCallback(
    e => {
      e?.stopPropagation();
      setActiveIdx(i => (i === null ? null : (i + 1) % gallery.length));
    },
    [gallery.length]
  );

  useEffect(() => {
    if (activeIdx === null) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [activeIdx, close, prev, next]);

  if (!project) {
    return (
      <div>
        <SEO title="Project not found - Decorzo Space" />
        <Description>
          <div className="desc-inner">
            <p className="desc-body">This project couldn't be found.</p>
          </div>
        </Description>
        <Footer />
      </div>
    );
  }

  const heroImg = project.coverImage?.asset?.url;
  const activeImg = activeIdx !== null ? gallery[activeIdx]?.asset?.url : null;

  const galleryCount = gallery.length;
  const rows = buildRows(gallery);
  // Track running index for tile numbering
  let runningIdx = -1;

  return (
    <div>
      <SEO
        title={`${project.title} - Decorzo Space`}
        description={project.description}
      />

      <Hero>
        {heroImg && (
          <img
            className="hero-img"
            src={heroImg + '?w=1920&auto=format'}
            alt={project.title}
          />
        )}
        <div className="hero-overlay" />
        <div className="hero-content">
          {project.category?.name && (
            <div className="hero-cat">{project.category.name}</div>
          )}
          <h1 className="hero-title">{project.title}</h1>
          <div className="hero-meta">
            <span>Portfolio</span>
            <span className="dot" />
            <span>
              {galleryCount} {galleryCount === 1 ? 'View' : 'Views'}
            </span>
          </div>
        </div>
      </Hero>

      <Crumbs>
        <div className="crumbs-inner">
          <Link to="/portfolio" className="crumb-back">
            <svg viewBox="0 0 24 24">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            All Projects
          </Link>
          {project.category?.name && (
            <span className="crumb-cat">{project.category.name}</span>
          )}
        </div>
      </Crumbs>

      {project.description && (
        <Description>
          <div className="desc-inner">
            <div className="desc-label">About the Project</div>
            <p className="desc-body">{project.description}</p>
          </div>
        </Description>
      )}

      {galleryCount > 0 && (
        <Gallery>
          <div className="gallery-inner">
            <div className="gallery-section-label">Project Gallery</div>
            {rows.map((row, rIdx) => (
              <Row key={rIdx} cols={row.cols}>
                {row.items.map(img => {
                  runningIdx += 1;
                  const idx = runningIdx;
                  return (
                    <Tile
                      key={img._key || img.asset?._id || idx}
                      ratio={row.ratio}
                      onClick={() => setActiveIdx(idx)}
                    >
                      <span className="tile-num">
                        {String(idx + 1).padStart(2, '0')} /{' '}
                        {String(galleryCount).padStart(2, '0')}
                      </span>
                      {img.asset?.url && (
                        <img
                          src={img.asset.url + '?w=1600&auto=format'}
                          alt={`${project.title} — ${idx + 1}`}
                          loading="lazy"
                        />
                      )}
                    </Tile>
                  );
                })}
              </Row>
            ))}
          </div>
        </Gallery>
      )}

      <CtaSection>
        <div className="cta-eyebrow">Let's collaborate</div>
        <h2 className="cta-title">
          Have a space that needs a thoughtful touch?
        </h2>
        <div className="cta-actions">
          <Link to="/contact" className="cta-btn primary">
            Start a Project
            <svg viewBox="0 0 24 24">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
          <Link to="/portfolio" className="cta-btn ghost">
            <svg viewBox="0 0 24 24">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            View More Projects
          </Link>
        </div>
      </CtaSection>

      {activeImg && (
        <Lightbox onClick={close}>
          <span className="lb-counter">
            {String(activeIdx + 1).padStart(2, '0')} /{' '}
            {String(galleryCount).padStart(2, '0')}
          </span>
          <img src={activeImg + '?w=1920&auto=format'} alt="" />
          {gallery.length > 1 && (
            <>
              <button
                className="lb-prev"
                onClick={prev}
                aria-label="Previous image"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                className="lb-next"
                onClick={next}
                aria-label="Next image"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </>
          )}
          <button className="lb-close" onClick={close} aria-label="Close">
            <svg viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </Lightbox>
      )}

      <Footer />
    </div>
  );
}
